'use client';

import { useEffect } from 'react';
import {
  announcePreviewReady,
  applyPreviewThemeMode,
  previewAllowedOrigins,
  readPreviewConfigUpdate,
} from './previewProtocol';

export interface PagePreviewHandshakeProps {
  /**
   * Origins allowed to drive the preview. Omitted: the
   * {@code NEXT_PUBLIC_PREVIEW_ALLOWED_ORIGINS} the host was built with.
   */
  allowedOrigins?: string | readonly string[];
}

/**
 * The client half of a host's {@code /studio-preview/page} route. Mount it
 * after the rendered page: it tells the admin the preview is ready, then
 * applies theme-mode changes the admin sends.
 *
 * <p>Locale and content changes are not applied in place — the admin reloads
 * the frame with a new query string, so the preview is always what the
 * renderer produced.
 */
export function PagePreviewHandshake({ allowedOrigins }: PagePreviewHandshakeProps) {
  const listed = allowedOrigins ?? process.env.NEXT_PUBLIC_PREVIEW_ALLOWED_ORIGINS;
  const key = typeof listed === 'string' ? listed : (listed ?? []).join(',');

  useEffect(() => {
    const origins = previewAllowedOrigins(key, window.location.origin);
    const onMessage = (event: MessageEvent) => {
      const update = readPreviewConfigUpdate(event, origins);
      if (update?.themeMode) applyPreviewThemeMode(update.themeMode);
    };
    window.addEventListener('message', onMessage);
    announcePreviewReady();
    return () => window.removeEventListener('message', onMessage);
  }, [key]);

  return null;
}
